<script setup>
import { reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import NextInput from '@/components/Form/NextInput.vue'
import Button from '@/components/Button.vue'
import { useAlert } from '@/composables/useToast'

const emit = defineEmits(['close'])

const store = useStore()
const { t } = useI18n()

const form = reactive({
  name: '',
  access_token: '',
  group_id: '',
})

const rules = computed(() => ({
  access_token: { required },
  group_id: { required },
}))

const v$ = useVuelidate(rules, form)

const uiFlags = computed(() => store.getters['inboxes/getUIFlags'])

const submit = async () => {
  v$.value.$touch()
  if (v$.value.$invalid) return
  try {
    await store.dispatch('inboxes/createVk', { ...form })
    useAlert(t('INBOX_MGMT.ADD.VK.API.SUCCESS'))
    emit('close')
  } catch (error) {
    useAlert(
      error?.response?.status === 422
        ? t('INBOX_MGMT.ADD.VK.API.INVALID_TOKEN')
        : t('INBOX_MGMT.ADD.VK.API.ERROR_MESSAGE')
    )
  }
}
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="submit">
    <p class="text-body-main text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.VK.HELP') }}
    </p>

    <NextInput
      v-model="form.access_token"
      name="vk_access_token"
      type="text"
      required
      :label="$t('INBOX_MGMT.ADD.VK.TOKEN.LABEL')"
      :placeholder="$t('INBOX_MGMT.ADD.VK.TOKEN.PLACEHOLDER')"
      :has-error="v$.access_token.$error"
      @input="v$.access_token.$touch"
    />

    <NextInput
      v-model="form.group_id"
      name="vk_group_id"
      type="text"
      required
      :label="$t('INBOX_MGMT.ADD.VK.GROUP.LABEL')"
      :placeholder="$t('INBOX_MGMT.ADD.VK.GROUP.PLACEHOLDER')"
      :has-error="v$.group_id.$error"
      @input="v$.group_id.$touch"
    />

    <NextInput
      v-model="form.name"
      name="vk_inbox_name"
      type="text"
      :label="$t('INBOX_MGMT.ADD.VK.NAME.LABEL')"
      :placeholder="$t('INBOX_MGMT.ADD.VK.NAME.PLACEHOLDER')"
    />

    <div class="flex justify-end gap-2 pt-2">
      <Button
        faded
        slate
        type="button"
        :label="$t('INBOX_MGMT.ADD.CANCEL')"
        @click="emit('close')"
      />
      <Button
        type="submit"
        :label="$t('INBOX_MGMT.ADD.SUBMIT')"
        :disabled="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
